/**
 * Recursively searches an object tree to find an item by containerId name
 * @param {Object} obj - Object tree to search
 * @param {string} name - Name to search for containerId
 * @return {Object|null} Found item or null
 */
export function findItemByContainerId(obj, name) {
  // If we have :items, check them first
  if (obj[":items"]) {
    // Look through each key in :items
    for (const key of Object.keys(obj[":items"])) {
      // If item containerId matches with name
      if (
        obj[":items"][key]?.containerId &&
        obj[":items"][key].containerId === name
      ) {
        return obj[":items"][key]
      }
      // If this item has nested :items, recurse into them
      const result = findItemByContainerId(obj[":items"][key], name)
      if (result) return result
    }
  }
  return null
}
/**
 * Extracts and formats promo banner content and CTAs from a container(navigation object).
 *
 * @param {Object} container - The container/navigation object containing promo data.
 * @param {string} name - The name or containerId to locate the promo details.
 * @param {Function} setPromoModal - Callback to trigger modal display for a promo.
 * @return {Array<Object>} - An array of formatted promo banner objects.
 */
export function extractPromoBanners(container, name, setPromoModal) {
  // Find item by containerId
  const promoDetails = findItemByContainerId(container, name)
  if (!promoDetails) {
    return []
  }
  // Extract promo banner items
  const promoBanners =
    promoDetails?.[":items"]?.["carousel_slick"]?.[
      "carouselComponentProperties"
    ]?.[":items"]
  if (!promoBanners) {
    return []
  }
  // Format the response
  return Object.values(promoBanners).map(banner => {
    const content = banner.description?.replace(/<[^>]*>/g, "").trim() || ""
    const CTAs = (banner.actionItems || []).map(item => {
      let ctaObj = {
        text: item.text,
        href: item.ctaLink,
      }
      if (item.enablePopup && banner.actionsEnabled) {
        // open popup
        ctaObj = {
          text: item.text,
          onClick: () => {
            setPromoModal({
              show: true,
              modalContent: {
                banner: banner,
                item: item,
              },
            })
          },
        }
      } else if (item.isNewTab && !item.enablePopup) {
        // open in new tab
        ctaObj = {
          text: item.text,
          onClick: () => window.open(item.ctaLink, "_blank"),
        }
      }
      ctaObj.trackingAttributes = {
        "data-datalayer-id": "kds-element-navigation",
        "data-datalayer-data": JSON.stringify({
          clickInternalLinks: item.enablePopup ? "true" : "false",
          eventAction: item.text,
          eventName: item.text,
          internalLinkName: item.text,
          internalLinkPosition:
            typeof window !== "undefined"
              ? `${window.eventPageType}:promo banner`
              : "plp:promo banner",
          internalLinkType: item.enablePopup ? "navigation" : ":",
          internalLinkURL: item.enablePopup
            ? "n/a"
            : item.ctaLink && typeof window !== "undefined"
            ? `${window.location.origin}${item.ctaLink
                ?.split(process.env.NEXT_PUBLIC_INTERNALLINKPATH)
                .join("")
                .replace(".html", "")}`
            : "n/a",
          internalLinkZoneName:
            typeof window !== "undefined"
              ? `${window.eventPageType}:promo details`
              : "plp:promo details",
          eventMsg: "n/a",
          eventStatus: "n/a",
          eventType: "promo banner",
        }),
      }
      return ctaObj
    })
    return { content, CTAs }
  })
}
